/**
 * Human-readable labels for internal fact / lever / channel KEYS.
 *
 * Rule (product): we NEVER surface a raw variable name (goal_priority, uk_primary_residence_sellable,
 * web_levers, …) to a user. Everything shown reads as plain words in the member's language. This is the single place that maps
 * a machine key to its user-facing label, reused by the plan thought-log, the dashboard, and any prose
 * that might otherwise leak a key. When a key isn't mapped, we title-case it as a safe fallback so the
 * worst case is "Goal Priority", never "goal_priority".
 */

import { getMyCIOPlanDetailsCopy } from '@/lib/mycio/copy/planDetails'

// The labels themselves live in copy/planDetails.ts (`keyLabels`, `channelLabels`), in the member's language.

// Country/region prefixes we strip from real-asset keys (uk_primary_residence_sellable -> primary residence).
const REGION_PREFIX = /^(uk|us|usa|gb|ca|au|nz|eu|ch|de|fr|it|es|nl|ie|jp|sg|hk|ae|za|in|cn)_/i

function titleCase(raw: string): string {
  const words = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().split(' ')
  return words
    .map((w, i) => {
      const upper = w.toUpperCase()
      // Keep well-known acronyms upper-cased; sentence-case the rest (only the first word capitalised).
      if (['us', 'uk', 'eu', 'ira', 'hsa', 'roth', '401k', 'cgt', 'ss'].includes(w.toLowerCase())) return upper
      return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w
    })
    .join(' ')
}

/** Map a raw fact/lever key to its human label (mapped first, region-stripped, then title-cased). */
export function humanizeKey(key: string, language?: string | null): string {
  const k = String(key || '').trim()
  if (!k) return ''
  const labels = getMyCIOPlanDetailsCopy(language).keyLabels
  if (labels[k]) return labels[k]
  const stripped = k.replace(REGION_PREFIX, '')
  if (labels[stripped]) return labels[stripped]
  // No settled label: a field label in the member's language when one exists, else the key's own words.
  const field = language ? getMyCIOPlanDetailsCopy(language).fieldLabels[stripped] : undefined
  if (field && getMyCIOPlanDetailsCopy('en').fieldLabels[stripped] !== field) return field
  return titleCase(stripped)
}

/** Map a channel/tool tag to a friendly source label (unmapped -> title-cased). */
export function humanizeChannel(tool?: string | null, language?: string | null): string {
  const t = String(tool || '').trim()
  if (!t) return ''
  return getMyCIOPlanDetailsCopy(language).channelLabels[t] || titleCase(t)
}

// A snake_case token that looks like an internal key: 2+ segments of lowercase/digits joined by "_".
const KEY_TOKEN = /(['"`]?)\b([a-z][a-z0-9]*(?:_[a-z0-9]+)+)\b(['"`]?)/g

/**
 * Replace any raw snake_case key that leaks into a prose string with its human label. Also unwraps the
 * quotes around a quoted key ('goal_priority' -> Goal ranking). Safe on normal prose: only tokens that
 * are genuinely snake_case (contain an underscore between word chars) are touched.
 */
export function stripKeyNames(text: string, language?: string | null): string {
  const s = String(text || '')
  if (!s || s.indexOf('_') === -1) return s
  // In the member's language: a key inside non-English prose becomes that language's label, never an
  // English one (an unmapped key keeps its own words, as it always did).
  return s.replace(KEY_TOKEN, (_m, _q1, token) => humanizeKey(token, language))
}
